import { jobPositionText } from './job-crew-positions';

/**
 * Crew identity for the per-line tech slots.
 *
 * WHY THE SLOTS NOW CARRY AN ID. A tech slot held two characters of free text
 * and nothing else, so a crew with two people sharing initials could not be
 * written down. TNT has two Salyers — Cameron (field worker) and Christopher
 * (admin) — both on the crew of one job, and one $4,825 torque_monitoring line
 * on FT-04403 carries `CS` in BOTH slots. No amount of care resolves that
 * afterwards: the token cannot say which brother, and one of them holds a pay
 * rate for that line type while the other holds none at all.
 *
 * So the option VALUE is the user id and the option LABEL is still initials.
 * The initials go on being written beside the id — they are what the ticket
 * PDF and the read-only views render, and an offline device running an older
 * bundle still sends and reads them.
 *
 * INITIALS ARE DISPLAY ONLY, AND DERIVED. `users.initials` is NULL for every
 * user at every live tenant, so `crewInitials` falls back to first+last. That
 * derivation is safe HERE because it only ever produces a label. Deriving on
 * the write path was considered and rejected outright — it manufactures the CS
 * collision rather than recording it, and a partial derivation is worse still,
 * because matching one of two named hands changes the crew-size tier and moves
 * real money.
 */

/**
 * A crew member as it arrives in `tickets.crew` / `jobs.crew` jsonb.
 *
 * BOTH `userId` AND `id` ARE LIVE IN THAT COLUMN and the || chain below is
 * load-bearing, not defensive habit: FT-04418 stores `userId` while FT-04419
 * — three days later, same tenant — stores `id`. Dropping either spelling
 * silently un-identifies half the crew rosters in production.
 */
export interface CrewMemberLike {
  userId?: string | null;
  id?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  initials?: string | null;
  /** The hand's position ON THIS JOB (API 0426), either casing. */
  positions?: unknown;
}

/** The stable identity of a crew member, or '' if the row carries neither key. */
export function crewId(c: CrewMemberLike): string {
  return String(c.userId || c.id || '');
}

/** Display token: stored initials when present, otherwise derived from the name. */
export function crewInitials(c: CrewMemberLike): string {
  if (c.initials && c.initials.trim()) return c.initials.trim();
  const first = c.first_name || (c.name ? c.name.split(' ')[0] : '') || '';
  const last = c.last_name || (c.name ? c.name.split(' ').slice(-1)[0] : '') || '';
  const derived = `${first[0] || ''}${last[0] || ''}`.toUpperCase();
  return derived || (c.name || '').trim();
}

export function crewName(c: CrewMemberLike): string {
  return c.name || [c.first_name, c.last_name].filter(Boolean).join(' ') || '';
}

/**
 * `PH — PieterJan Hoogendoorn · Tong Hand`. The name disambiguates two people
 * sharing a token; the job position (0426), when one is picked, says which of
 * the crew a line belongs to. No position picked → no suffix, never a blank one.
 */
export function crewLabel(c: CrewMemberLike): string {
  const name = crewName(c);
  const token = crewInitials(c);
  const base = name ? `${token} — ${name}` : token;
  const position = jobPositionText(c.positions);
  return position ? `${base} · ${position}` : base;
}

/** Find a crew member by id. Returns undefined rather than guessing. */
export function findCrewById(crew: CrewMemberLike[], id: string | null | undefined) {
  if (!id) return undefined;
  return crew.find(c => crewId(c) === id);
}

export type TechSlot = 1 | 2 | 3 | 4;
export const TECH_SLOTS: readonly TechSlot[] = [1, 2, 3, 4];

/** The tech-slot half of a ticket line — the eight keys both editors carry. */
export interface TechSlotFields {
  assignedTech1Id: string | null;
  assignedTech2Id: string | null;
  assignedTech3Id: string | null;
  assignedTech4Id: string | null;
  assignedTech1Initials: string | null;
  assignedTech2Initials: string | null;
  assignedTech3Initials: string | null;
  assignedTech4Initials: string | null;
}

/**
 * Put one person in one slot: the id AND the initials, in one object, so a row
 * can never carry one without the other. THE ONLY WRITER of a slot — both
 * editors' pickers and the job-position pre-fill (lib/ticket-slot-prefill.ts)
 * go through it. `member` is who `userId` names in the picker's lists; absent,
 * the initials are null rather than a stale token from the last occupant.
 */
export function assignTechSlot<T extends TechSlotFields>(
  line: T,
  slot: TechSlot,
  userId: string | null,
  member: CrewMemberLike | undefined,
): T {
  return {
    ...line,
    [`assignedTech${slot}Id`]: userId,
    [`assignedTech${slot}Initials`]: userId && member ? crewInitials(member) : null,
  };
}
